import { FC, Fragment, ReactNode } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

export interface LegendItem {
  /** Stable list key; falls back to the item index when omitted. */
  id?: string;
  /** Leading visual inside the pill. When omitted, `color` renders a dot. */
  icon?: ReactNode;
  /** Pill text/icon colour. */
  color?: string;
  /** Pill fill. */
  backgroundColor?: string;
  /** Pill outline. */
  borderColor?: string;
  /** The thing being explained, shown as a pill (e.g. an action or a status). */
  label: ReactNode;
  /** What it means, in plain language. */
  description?: ReactNode;
}

export interface LegendProps {
  items: LegendItem[];
  /** Optional heading above the items. */
  title?: ReactNode;
  /** Icon for the accent badge beside the title. */
  titleIcon?: ReactNode;
  /**
   * `column` (default) aligns the pills in a shared track so every description
   * starts at the same edge — best for sentence-length explanations. `row`
   * flows short entries across the available width instead.
   */
  direction?: 'row' | 'column';
  sx?: SxProps<Theme>;
}

const Pill: FC<{ item: LegendItem }> = observer(({ item }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      flex: 'none',
      typography: 'textXS_semiBold',
      color: item.color ?? 'text.secondary',
      backgroundColor: item.backgroundColor ?? 'grey.200',
      border: 1,
      borderColor: item.borderColor ?? 'transparent',
      borderRadius: 1.5,
      px: 1,
      py: 0.375,
      whiteSpace: 'nowrap',
    }}
  >
    {item.icon ?? (
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' }}
      />
    )}
    {item.label}
  </Box>
));

const Description: FC<{ children: ReactNode }> = observer(({ children }) => (
  <Typography variant="textXS_regular" color="text.secondary">
    {children}
  </Typography>
));

/**
 * Reusable key/legend card: a titled panel of entries — each an action/status
 * pill and a plain-language explanation — for screens that need their actions,
 * statuses or colour-coding spelled out up front. Purely informational: it
 * renders no controls and never affects the surrounding flow.
 */
export const Legend: FC<LegendProps> = observer(
  ({ items, title, titleIcon, direction = 'column', sx }) => (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderRadius: 2.5,
        border: 1,
        borderColor: 'blue.200',
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.blue[100]} 0%, ${theme.palette.background.paper} 65%)`,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        ...sx,
      }}
    >
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          {titleIcon && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                width: 16,
                height: 16,
                borderRadius: 1,
                backgroundColor: 'primary.main',
                color: 'common.white',
              }}
            >
              {titleIcon}
            </Box>
          )}
          <Typography variant="textXS_semiBold" color="primary.dark">
            {title}
          </Typography>
        </Box>
      )}

      {direction === 'column' ? (
        // Two tracks: the pills share a column, so every description lines up.
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            alignItems: 'center',
            columnGap: 1.5,
            rowGap: 1.5,
          }}
        >
          {items.map((item, index) => (
            <Fragment key={item.id ?? index}>
              {index > 0 && (
                <Divider sx={{ gridColumn: '1 / -1', borderColor: 'blue.200', opacity: 0.6 }} />
              )}
              <Pill item={item} />
              <Description>{item.description}</Description>
            </Fragment>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 2,
            rowGap: 0.75,
          }}
        >
          {items.map((item, index) => (
            <Box
              key={item.id ?? index}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
            >
              <Pill item={item} />
              <Description>{item.description}</Description>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  ),
);
